import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ExploreBackHeader, ExploreSubpageCard, useStandaloneExploreHeader } from "./areaShared";
import { listMyDialogues } from "../../Services/dialogues";
import { resolveLifeArea, useLifeAreas } from "./lifeAreas";
import { LIST_TABS, cardMeta, tabFromParam, tabParam } from "./lifecycle";
import { ACTIVE_LIST_COPY } from "./copy";
import chevronIcon from "../../Assets/icon/explore/chevron-right.svg";
import "./style.css";
import "./activeDialogues.css";

/**
 * Active Dialogues list (Figma 11115:28057 / 28117 / 28167) — "View All" from
 * the Explore rail. `/explore/active-dialogues?tab=active|paused|completed|archived`.
 *
 * One `GET /ARIS/v4/dialogues/instances?state=…` per tab; it also returns the
 * counts for every tab, so the tab labels stay right whichever tab is open.
 * Items render in the server's order (most recent first by the tab's date).
 *
 * A card opens the standard Dialogue Detail by `dialogue_id` — a template id
 * or a research id (CDLG-…); the Overview route resolves both, which is how
 * research Dialogues open everywhere else on the web. `/explore/research/:id`
 * is the "Research Behind This Dialogue" page, not the Dialogue.
 */

function Thumb({ src }) {
  const [ok, setOk] = useState(false);
  useEffect(() => setOk(false), [src]);
  return (
    <span className="exa-card__media">
      {src && (
        <img
          src={src}
          alt=""
          className={ok ? "exa-card__img exa-card__img--ready" : "exa-card__img"}
          onLoad={() => setOk(true)}
          onError={() => setOk(false)}
        />
      )}
    </span>
  );
}

function DialogueListCard({ item, tab, onOpen }) {
  const area = item.display_area_key ? resolveLifeArea({ key: item.display_area_key }) : null;
  const meta = cardMeta(item, tab);
  const openable = Boolean(item.dialogue_id);
  return (
    <li>
      <button
        type="button"
        className="exa-card"
        disabled={!openable}
        onClick={() => openable && onOpen(item)}
      >
        {/* Plan 49: the 76x82 list crop; the rail's 240x100 strip as a fallback. */}
        <Thumb src={item.thumbnail_url || item.image_url} />
        <span className="exa-card__text">
          <span className="exa-card__title">{item.title}</span>
          {area && <span className="exa-card__chip">{area.title}</span>}
          {meta && <span className="exa-card__meta">{meta}</span>}
        </span>
        {openable && (
          <span className="exa-card__chevron" aria-hidden="true">
            <img src={chevronIcon} alt="" width="16" height="16" />
          </span>
        )}
      </button>
    </li>
  );
}

function ActiveDialoguesPage() {
  useLifeAreas(); // re-render once the Life Areas arrive
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useStandaloneExploreHeader();

  const tab = tabFromParam(searchParams.get("tab"));
  const [state, setState] = useState({ loading: true, error: false, items: [] });
  const [counts, setCounts] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const tabRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: false, items: [] });
    (async () => {
      try {
        const data = await listMyDialogues(tab);
        if (cancelled) return;
        setState({ loading: false, error: false, items: data?.items || [] });
        if (data?.counts) setCounts(data.counts);
      } catch (e) {
        console.error("Failed to load the member's dialogues", e);
        if (!cancelled) setState({ loading: false, error: true, items: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, attempt]);

  const selectTab = useCallback(
    (next) => setSearchParams({ tab: tabParam(next) }, { replace: true }),
    [setSearchParams]
  );

  // Arrow keys move between tabs (WAI-ARIA tabs pattern).
  const onTabKey = (e) => {
    const i = LIST_TABS.indexOf(tab);
    let next = null;
    if (e.key === "ArrowRight") next = LIST_TABS[(i + 1) % LIST_TABS.length];
    if (e.key === "ArrowLeft") next = LIST_TABS[(i - 1 + LIST_TABS.length) % LIST_TABS.length];
    if (e.key === "Home") next = LIST_TABS[0];
    if (e.key === "End") next = LIST_TABS[LIST_TABS.length - 1];
    if (!next) return;
    e.preventDefault();
    selectTab(next);
    tabRefs.current[next]?.focus();
  };

  const open = (item) => navigate(`/explore/dialogue/${encodeURIComponent(item.dialogue_id)}`);

  let body;
  if (state.loading) {
    body = (
      <ul className="exa-list" aria-busy="true" aria-label={ACTIVE_LIST_COPY.loading}>
        {[0, 1, 2].map((i) => (
          <li key={i} className="exa-card exa-card--skeleton" data-testid="exa-skeleton">
            <span className="exa-card__media" />
            <span className="exa-card__text">
              <span className="exa-skel exa-skel--title" />
              <span className="exa-skel exa-skel--chip" />
              <span className="exa-skel exa-skel--meta" />
            </span>
          </li>
        ))}
      </ul>
    );
  } else if (state.error) {
    body = (
      <div className="explore-card explore-card--status exa-status" role="alert">
        <p className="exa-status__text">{ACTIVE_LIST_COPY.error}</p>
        <button type="button" className="exa-retry" onClick={() => setAttempt((n) => n + 1)}>
          {ACTIVE_LIST_COPY.retry}
        </button>
      </div>
    );
  } else if (state.items.length === 0) {
    body = (
      <div className="explore-card explore-card--status exa-status">
        {ACTIVE_LIST_COPY.empty[tab]}
      </div>
    );
  } else {
    body = (
      <ul className="exa-list">
        {state.items.map((item) => (
          <DialogueListCard
            key={item.dialogue_instance_id || item.dialogue_id}
            item={item}
            tab={tab}
            onOpen={open}
          />
        ))}
      </ul>
    );
  }

  return (
    <div className="explore-page exa">
      <div className="explore-page__inner">
        <ExploreBackHeader to="/explore" />
        <ExploreSubpageCard>
          <h1 className="explore-page__title exa__title">{ACTIVE_LIST_COPY.title}</h1>
          <div
            className="exa-tabs"
            role="tablist"
            aria-label={ACTIVE_LIST_COPY.tabsLabel}
            onKeyDown={onTabKey}
          >
            {LIST_TABS.map((t) => {
              const label = ACTIVE_LIST_COPY.tabs[t];
              const n = counts?.[t];
              const selected = t === tab;
              return (
                <button
                  key={t}
                  ref={(el) => {
                    tabRefs.current[t] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`exa-tab-${tabParam(t)}`}
                  aria-selected={selected}
                  aria-controls="exa-panel"
                  tabIndex={selected ? 0 : -1}
                  className={`exa-tab${selected ? " exa-tab--selected" : ""}`}
                  onClick={() => selectTab(t)}
                >
                  {Number.isFinite(n) ? ACTIVE_LIST_COPY.tabWithCount(label, n) : label}
                </button>
              );
            })}
          </div>
          <div id="exa-panel" role="tabpanel" aria-labelledby={`exa-tab-${tabParam(tab)}`}>
            {body}
          </div>
        </ExploreSubpageCard>
      </div>
    </div>
  );
}

export default ActiveDialoguesPage;
